/**
 * Cryptographically strong opaque tokens for invite / stub / timetable links.
 *
 * Uses the Web Crypto API (`crypto.getRandomValues`), available in browsers and
 * Node 18+, because the DAL is bundled into the admin app and runs in the
 * browser as well as on the API server — Node's `crypto.randomBytes` is not
 * available in that browser context. Mirrors the guard already used by
 * `generateThingCode` in index.ts.
 *
 * Tokens are looked up by exact value, so widening the alphabet/length here is
 * backward-compatible: previously issued (weaker) tokens keep validating; only
 * newly minted tokens gain the extra entropy.
 */

// 32 bytes = 256 bits of entropy, hex-encoded to a 64-char URL-safe string.
export const secureToken = (bytes = 32): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
        const buf = new Uint8Array(bytes);
        crypto.getRandomValues(buf);
        return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    // Fallback for runtimes without Web Crypto (very old Node). Weaker, but keeps
    // token generation working; all supported deployment runtimes expose crypto.
    return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
};
